/**
 * Low-level Gemini Generative Language API fetch wrapper.
 *
 * Responsibilities:
 *   - Construct `generateContent` / `streamGenerateContent` HTTPS requests
 *     (Requirements 5.8, 6.1–6.3).
 *   - Apply the strictest free-tier safety thresholds on every outbound
 *     request body (Requirement 14.3 / Property 18).
 *   - Parse Server-Sent-Event chunks and yield typed events suitable for
 *     the Model_Router executor's pure state machine
 *     (Requirements 5.6, 5.8, 5.9).
 *   - Read the API key from the build-time env var or a runtime override
 *     supplied by the Setup screen, with a proxy mode that keeps the key
 *     out of the browser bundle entirely (Requirements 6.1, 6.2, 6.3, 6.7).
 *   - Never log the API key in any form and never let it appear in the
 *     `reason` field of yielded error events (Requirement 6.8).
 *   - Never throw — every error path becomes an event the executor can
 *     drive a clean state machine over.
 *
 * The generator yields zero-or-more `chunk` events followed by exactly one
 * terminal event in `{ complete, rateLimit, transient, safetyBlock,
 * streamInterrupted }`.
 */

import type { ModelId } from '../domain/modelRouter/types';
import { MODEL_API_NAMES } from '../domain/modelRouter/fallbackChain';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * One turn in the Gemini `contents` array. Roles match the wire format
 * exactly: the End_User is `'user'`; AI_Companion replies are `'model'`.
 *
 * Parts can be text or inline image data (base64-encoded). Gemini's
 * multimodal API accepts `inlineData` parts alongside `text` parts in
 * the same turn.
 */
export type GeminiPart =
  | { text: string }
  | { inlineData: { mimeType: string; data: string } };

export interface GeminiContent {
  role: 'user' | 'model';
  parts: GeminiPart[];
}

/**
 * Input to `sendGeminiRequest`.
 *
 * `systemInstruction` is wired through to Gemini's top-level
 * `systemInstruction` field so the Prompt_Builder's composed System_Prompt
 * is delivered as a system message rather than smuggled into the user
 * turns. `stream === true` selects the SSE streaming endpoint; `false`
 * uses the unary endpoint and a single `complete` event.
 */
export interface GeminiRequest {
  modelId: ModelId;
  systemInstruction: string;
  contents: GeminiContent[];
  /** Stream chunks vs. wait for the full response. */
  stream: boolean;
  /** Optional caller AbortSignal merged with internal timeout aborts. */
  signal?: AbortSignal;
  /** Enable Google Search grounding for web-aware responses. */
  useWebSearch?: boolean;
}

/**
 * Discriminated union of events produced by `sendGeminiRequest`.
 *
 *  - `chunk`              — incremental token text from a streaming response.
 *  - `complete`           — terminal success; `fullText` is the assembled body.
 *  - `rateLimit`          — HTTP 429; `retryAfterSec` is parsed from
 *                           `Retry-After` when present.
 *  - `transient`          — 5xx, network failure, malformed JSON, or unknown
 *                           non-quota error. `reason` is a redacted human-
 *                           readable label suitable for the diagnostic log.
 *  - `safetyBlock`        — Gemini returned a `promptFeedback.blockReason`
 *                           or empty `candidates`; `reason` carries the
 *                           server-supplied label.
 *  - `streamInterrupted`  — the SSE stream errored mid-flight; the partial
 *                           text accumulated so far is returned so the
 *                           executor can swap it for a complete reply.
 */
export type GeminiResponseEvent =
  | { kind: 'chunk'; text: string }
  | { kind: 'complete'; fullText: string }
  | { kind: 'rateLimit'; retryAfterSec?: number }
  | { kind: 'transient'; reason: string }
  | { kind: 'safetyBlock'; reason: string }
  | { kind: 'streamInterrupted'; partialText: string };

// ---------------------------------------------------------------------------
// API key handling
// ---------------------------------------------------------------------------

/**
 * Module-private runtime override for the API key.
 *
 * Set by the Setup screen via `setApiKeyOverride` after the End_User
 * pastes a key (Requirement 6.7); takes precedence over the build-time
 * env var so the End_User can rotate keys without rebuilding.
 */
let apiKeyOverride: string | null = null;

/**
 * Cached pool of API keys derived from the build-time env. `null` until
 * the first read so a key rotation in the Setup screen can invalidate it.
 */
let keyPool: string[] | null = null;

/**
 * Index of the key currently in use within {@link getKeyPool}'s result.
 * Advanced by {@link advanceApiKey} whenever the active key hits a
 * rate-limit (HTTP 429) so the next attempt transparently uses a backup
 * key — multiplying the effective free-tier daily quota.
 */
let currentKeyIndex = 0;

/**
 * Parse every configured Gemini API key into an ordered, de-duplicated
 * pool.
 *
 * Two ways to configure multiple keys (both optional, combined):
 *   1. `VITE_GEMINI_API_KEY` may itself hold several keys separated by
 *      commas, spaces, or newlines.
 *   2. `VITE_GEMINI_API_KEY_2` … `VITE_GEMINI_API_KEY_9` — one extra key
 *      per numbered variable, for readability.
 *
 * The pool order is the rotation order: the primary key(s) first, then
 * the numbered backups.
 */
function readAllConfiguredKeys(): string[] {
  const keys: string[] = [];

  const primary = readEnv('VITE_GEMINI_API_KEY');
  if (primary !== null) {
    for (const candidate of primary.split(/[\s,]+/)) {
      const trimmed = candidate.trim();
      if (trimmed !== '') keys.push(trimmed);
    }
  }

  for (let i = 2; i <= 9; i += 1) {
    const extra = readEnv(`VITE_GEMINI_API_KEY_${i}`);
    if (extra !== null && extra.trim() !== '') {
      keys.push(extra.trim());
    }
  }

  // De-duplicate while preserving first-seen order.
  return [...new Set(keys)];
}

/**
 * Resolve the active key pool.
 *
 * A runtime override (Setup screen) takes precedence and is treated as a
 * single-key pool. Otherwise the env-derived pool is built once and
 * cached.
 */
function getKeyPool(): string[] {
  if (apiKeyOverride !== null && apiKeyOverride !== '') {
    return [apiKeyOverride];
  }
  if (keyPool === null) {
    keyPool = readAllConfiguredKeys();
  }
  return keyPool;
}

/**
 * Resolve the API key currently in use.
 *
 * Resolution order:
 *   1. Runtime override set by `setApiKeyOverride` (Setup screen, Requirement 6.7).
 *   2. The current entry of the build-time env key pool
 *      (`VITE_GEMINI_API_KEY` plus `VITE_GEMINI_API_KEY_2…9`), advanced
 *      by {@link advanceApiKey} on rate-limit.
 *   3. `null` — caller (App boot) routes the End_User to the Setup screen.
 *
 * Returns the raw string. The caller MUST NOT log or render the result;
 * use `isUsingProxy()` plus a "configured ✓ / not configured ✗" indicator
 * instead (Requirements 6.8, 13.5).
 */
export function getApiKey(): string | null {
  const pool = getKeyPool();
  if (pool.length === 0) {
    return null;
  }
  return pool[currentKeyIndex % pool.length] ?? null;
}

/** Number of distinct API keys configured. */
export function getApiKeyCount(): number {
  return getKeyPool().length;
}

/**
 * Rotate to the next configured API key.
 *
 * Returns `true` when there is more than one key (so the caller should
 * retry with the freshly-selected key) and `false` when only a single
 * key is configured (nothing to rotate to).
 */
export function advanceApiKey(): boolean {
  const pool = getKeyPool();
  if (pool.length <= 1) {
    return false;
  }
  currentKeyIndex = (currentKeyIndex + 1) % pool.length;
  return true;
}

/**
 * Set or clear the runtime API key override. Pass `null` to clear.
 *
 * The override is process-local and never persisted by this module;
 * the Setup screen is responsible for writing the key to the
 * Conversation_Store and invoking this setter on boot.
 */
export function setApiKeyOverride(key: string | null): void {
  apiKeyOverride = key;
  // Reset rotation so the next request starts from the top of the pool.
  currentKeyIndex = 0;
}

/**
 * `true` when `VITE_USE_PROXY === '1'`.
 *
 * In proxy mode the Chat_Client routes requests through a server-side
 * proxy at `VITE_PROXY_URL` that holds the Gemini key out-of-band, so
 * no key is sent from the browser (Requirement 6.3).
 */
export function isUsingProxy(): boolean {
  return readEnv('VITE_USE_PROXY') === '1';
}

// ---------------------------------------------------------------------------
// Request entry point
// ---------------------------------------------------------------------------

/**
 * Streaming Gemini API client.
 *
 * Issues a single Gemini request and yields a sequence of typed events.
 * The generator never throws: every failure becomes an event so the
 * Model_Router executor can drive its retry/cooldown state machine
 * without try/catch around an iterator (Requirement 5.6).
 *
 * Streaming behaviour (`request.stream === true`):
 *   1. POST `…:streamGenerateContent?alt=sse`.
 *   2. For each SSE `data:` payload, parse JSON, extract
 *      `candidates[0].content.parts[0].text`, yield `{ kind: 'chunk' }`,
 *      and accumulate to `fullText`.
 *   3. On clean stream end, yield `{ kind: 'complete', fullText }`.
 *   4. On mid-flight stream error (network drop, parse error, or 10-second
 *      idle without any new chunk), yield
 *      `{ kind: 'streamInterrupted', partialText }`.
 *
 * Non-streaming (`request.stream === false`):
 *   1. POST `…:generateContent`. 30-second total timeout via internal
 *      AbortController.
 *   2. Yield a single `{ kind: 'complete', fullText }` on success or
 *      one of the error events on failure.
 *
 * Common error mapping (both modes):
 *   - HTTP 429                     → `rateLimit` (parses `Retry-After`)
 *   - 5xx, fetch error, JSON error → `transient` (key-redacted reason)
 *   - blockReason / no candidates  → `safetyBlock`
 */
export async function* sendGeminiRequest(
  request: GeminiRequest,
): AsyncGenerator<GeminiResponseEvent> {
  // The internal abort controller lets us layer caller cancellation
  // (`request.signal`) with our own total / idle timeouts. We always
  // own the abort surface; the caller's signal only signals into ours.
  const internalController = new AbortController();
  const externalAbortHandler = (): void => internalController.abort();
  if (request.signal !== undefined) {
    if (request.signal.aborted) {
      internalController.abort();
    } else {
      request.signal.addEventListener('abort', externalAbortHandler, {
        once: true,
      });
    }
  }
  const cleanupExternal = (): void => {
    if (request.signal !== undefined) {
      request.signal.removeEventListener('abort', externalAbortHandler);
    }
  };

  // The 30-second non-streaming total timeout. For streaming, we
  // reset a separate idle timer per chunk instead so a long but
  // healthy reply isn't cut off by the wall-clock budget.
  const totalTimeoutMs = 30_000;
  const idleTimeoutMs = 10_000;

  // Build the URL + headers + body. Errors here are configuration bugs,
  // not network failures, so they surface as a redacted `transient`.
  const built = buildHttpRequest(request);
  if (built.kind === 'error') {
    cleanupExternal();
    yield { kind: 'transient', reason: built.reason };
    return;
  }
  const { url, init, redactKey } = built;
  init.signal = internalController.signal;

  let totalTimer: ReturnType<typeof setTimeout> | null = null;
  if (!request.stream) {
    totalTimer = setTimeout(() => internalController.abort(), totalTimeoutMs);
  }

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    if (totalTimer !== null) clearTimeout(totalTimer);
    cleanupExternal();
    yield { kind: 'transient', reason: redactKey(describeError(err)) };
    return;
  }

  if (!response.ok) {
    if (totalTimer !== null) clearTimeout(totalTimer);
    cleanupExternal();
    yield* mapHttpError(response, redactKey);
    return;
  }

  if (request.stream) {
    // Streaming reads the body as a chunked SSE byte stream.
    yield* readSseStream(response, internalController, idleTimeoutMs, redactKey);
    cleanupExternal();
    return;
  }

  // Non-streaming: read the full JSON body once, extract the text.
  let parsedBody: unknown;
  try {
    parsedBody = (await response.json()) as unknown;
  } catch (err) {
    if (totalTimer !== null) clearTimeout(totalTimer);
    cleanupExternal();
    yield { kind: 'transient', reason: redactKey(describeError(err)) };
    return;
  }
  if (totalTimer !== null) clearTimeout(totalTimer);
  cleanupExternal();

  const blockReason = extractBlockReason(parsedBody);
  if (blockReason !== null) {
    yield { kind: 'safetyBlock', reason: blockReason };
    return;
  }
  const text = extractText(parsedBody);
  if (text === null) {
    // Empty candidates without an explicit blockReason still indicates
    // a safety/empty-response situation per Requirement 12.3.
    yield { kind: 'safetyBlock', reason: 'no_candidates' };
    return;
  }
  yield { kind: 'complete', fullText: text };
}

// ---------------------------------------------------------------------------
// Internal: HTTP request construction
// ---------------------------------------------------------------------------

/**
 * Safety thresholds applied to every outbound request.
 *
 * This is a private, single-user companion app (Meryem's personal AI,
 * built by Enes). The End_User frequently vents with strong language —
 * e.g. swearing at her boyfriend — and expects the companion to take
 * her side and reply with warmth, not get silently swallowed by the
 * harassment filter. The previous `BLOCK_LOW_AND_ABOVE` thresholds
 * blocked these harmless emotional messages, which surfaced to the user
 * as the "Şu an yanıt alamadım — biraz sonra tekrar dene" soft-fallback.
 *
 * We set every category to `BLOCK_NONE` so the model stops refusing
 * profanity / venting. The Prompt_Builder's SAFETY block still steers
 * the companion away from genuinely harmful output (self-harm
 * encouragement, hate speech against protected groups, sexual content),
 * so behaviour stays appropriate without the API hard-blocking ordinary
 * frustrated messages.
 */
const SAFETY_SETTINGS = [
  { category: 'HARM_CATEGORY_HARASSMENT', threshold: 'BLOCK_NONE' },
  { category: 'HARM_CATEGORY_HATE_SPEECH', threshold: 'BLOCK_NONE' },
  { category: 'HARM_CATEGORY_SEXUALLY_EXPLICIT', threshold: 'BLOCK_NONE' },
  { category: 'HARM_CATEGORY_DANGEROUS_CONTENT', threshold: 'BLOCK_NONE' },
] as const;

/**
 * Conservative free-tier-friendly generation defaults. Tuned at design
 * time; the Prompt_Builder is the single source of stylistic control,
 * so these stay constant for every outbound request.
 */
const GENERATION_CONFIG = {
  // Lowered from 0.85 → 0.7 (and topP 0.95 → 0.9) to reduce token-level
  // garbling/incoherence observed with the lightweight workhorse model
  // (gemini-3.1-flash-lite), especially in Turkish. 0.7 keeps the voice
  // warm and natural while cutting low-probability "word-salad" output.
  temperature: 0.7,
  topP: 0.9,
  maxOutputTokens: 2048,
  // Gemini 2.5/3 modelleri thinkingConfig verilmezse varsayilan olarak
  // "dinamik dusunme" yapar ve bu cevaba birkac saniye gecikme ekler.
  // budget=0 ile flash/flash-lite modellerinde dusunmeyi kapatip
  // cevaplari hizlandiriyoruz.
  thinkingConfig: { thinkingBudget: 0 },
} as const;

const GEMINI_HOST = 'https://generativelanguage.googleapis.com';

interface BuiltRequest {
  kind: 'ok';
  url: string;
  init: RequestInit;
  /** Redacts the API key from arbitrary strings before they are yielded. */
  redactKey: (s: string) => string;
}

interface BuildError {
  kind: 'error';
  reason: string;
}

/**
 * Construct the URL, headers, and body for one Gemini request.
 *
 * In direct mode the API key is appended as the `key` query parameter
 * per Google's documented free-tier flow. In proxy mode the key never
 * leaves the proxy: we omit it from the URL and from headers entirely,
 * so a misconfigured proxy can't silently trip a key leak.
 */
function buildHttpRequest(request: GeminiRequest): BuiltRequest | BuildError {
  const proxy = isUsingProxy();
  const operation = request.stream
    ? 'streamGenerateContent'
    : 'generateContent';
  const sseSuffix = request.stream ? '?alt=sse' : '';

  let url: string;
  if (proxy) {
    const proxyBase = readEnv('VITE_PROXY_URL');
    if (proxyBase === null || proxyBase === '') {
      // Proxy mode requested but URL missing; treat as a configuration
      // failure that surfaces as a transient so the executor retries
      // the next model rather than crashing the UI.
      return {
        kind: 'error',
        reason: 'proxy_misconfigured',
      };
    }
    url = `${trimTrailingSlash(proxyBase)}/v1beta/models/${MODEL_API_NAMES[request.modelId] ?? request.modelId}:${operation}${sseSuffix}`;
  } else {
    const key = getApiKey();
    if (key === null) {
      return { kind: 'error', reason: 'no_api_key' };
    }
    const sep = sseSuffix === '' ? '?' : '&';
    const apiModelName = MODEL_API_NAMES[request.modelId] ?? request.modelId;
    url = `${GEMINI_HOST}/v1beta/models/${apiModelName}:${operation}${sseSuffix}${sep}key=${encodeURIComponent(key)}`;
  }

  const body: Record<string, unknown> = {
    contents: request.contents,
    systemInstruction: { parts: [{ text: request.systemInstruction }] },
    safetySettings: SAFETY_SETTINGS,
    generationConfig: GENERATION_CONFIG,
  };

  // Google Search grounding — enables real-time web search
  if (request.useWebSearch) {
    body['tools'] = [{ googleSearch: {} }];
  }

  const init: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };

  // Redactor strips the key (when there is one) from any string before
  // it reaches a yielded event. In proxy mode there is no key in the
  // URL or headers, so the redactor is a no-op.
  const directKey = proxy ? null : getApiKey();
  const redactKey = (s: string): string => {
    if (directKey === null || directKey === '') {
      return s;
    }
    // Both raw and url-encoded forms; URLs put the key through encodeURIComponent.
    return s
      .split(directKey)
      .join('***')
      .split(encodeURIComponent(directKey))
      .join('***');
  };

  return { kind: 'ok', url, init, redactKey };
}

// ---------------------------------------------------------------------------
// Internal: SSE streaming
// ---------------------------------------------------------------------------

/**
 * Read an SSE response body, yield text chunks, and produce exactly one
 * terminal event.
 *
 * The generator emits `streamInterrupted` (carrying the partial text
 * accumulated so far) on:
 *   - reader / decoder errors,
 *   - JSON parse failures inside an SSE event,
 *   - 10-second idle without any new bytes.
 *
 * It emits `complete` when the stream closes cleanly with no error, and
 * `safetyBlock` if the stream's first parsed event already carries a
 * `promptFeedback.blockReason`.
 */
async function* readSseStream(
  response: Response,
  controller: AbortController,
  idleTimeoutMs: number,
  redactKey: (s: string) => string,
): AsyncGenerator<GeminiResponseEvent> {
  const body = response.body;
  if (body === null) {
    yield { kind: 'streamInterrupted', partialText: '' };
    return;
  }

  // We pipe the byte stream through TextDecoderStream so we always
  // receive valid UTF-8 chunks even if a multi-byte char straddles a
  // network frame.
  let textStream: ReadableStream<string>;
  try {
    textStream = body.pipeThrough(new TextDecoderStream());
  } catch (err) {
    yield {
      kind: 'streamInterrupted',
      partialText: '',
    };
    void err; // explicit no-log: any err.message could include URL+key
    return;
  }

  const reader = textStream.getReader();
  let buffer = '';
  let fullText = '';
  let interrupted = false;
  let interruptReason: string | null = null;
  let safetyBlockReason: string | null = null;

  // Idle watchdog: if no bytes arrive for `idleTimeoutMs`, abort the
  // underlying fetch — the catch on `reader.read()` then yields
  // streamInterrupted.
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  const armIdle = (): void => {
    if (idleTimer !== null) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      interruptReason = 'idle_timeout';
      controller.abort();
    }, idleTimeoutMs);
  };
  const disarmIdle = (): void => {
    if (idleTimer !== null) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
  };

  armIdle();

  try {
    // We yield `chunk` events as we parse them out of the buffer. Any
    // parse / IO error sets `interrupted` and breaks the loop.
    while (true) {
      let readResult: ReadableStreamReadResult<string>;
      try {
        readResult = await reader.read();
      } catch (err) {
        interrupted = true;
        if (interruptReason === null) {
          interruptReason = redactKey(describeError(err));
        }
        break;
      }
      if (readResult.done) {
        break;
      }
      armIdle();

      buffer += readResult.value;

      // Process every complete SSE event in the buffer. Events are
      // separated by a blank line ("\n\n" or "\r\n\r\n").
      while (true) {
        const sep = findEventBoundary(buffer);
        if (sep === null) break;
        const rawEvent = buffer.slice(0, sep.index);
        buffer = buffer.slice(sep.endIndex);

        const dataLines = collectDataLines(rawEvent);
        if (dataLines.length === 0) continue;
        const payload = dataLines.join('\n');
        if (payload === '[DONE]') continue;

        let parsed: unknown;
        try {
          parsed = JSON.parse(payload) as unknown;
        } catch (err) {
          interrupted = true;
          interruptReason = redactKey(describeError(err));
          break;
        }

        const blockReason = extractBlockReason(parsed);
        if (blockReason !== null) {
          safetyBlockReason = blockReason;
          // Safety block during streaming: drain the rest of the
          // stream silently and emit a single safetyBlock event below.
          continue;
        }

        const chunkText = extractText(parsed);
        if (chunkText !== null && chunkText !== '') {
          fullText += chunkText;
          yield { kind: 'chunk', text: chunkText };
        }
      }
      if (interrupted) break;
    }
  } finally {
    disarmIdle();
    try {
      reader.releaseLock();
    } catch {
      // releaseLock throws only if the reader is in a weird state;
      // either way there's nothing useful for the caller.
    }
  }

  if (safetyBlockReason !== null) {
    yield { kind: 'safetyBlock', reason: safetyBlockReason };
    return;
  }

  if (interrupted) {
    void interruptReason; // captured for diagnostics; not surfaced verbatim
    yield { kind: 'streamInterrupted', partialText: fullText };
    return;
  }

  yield { kind: 'complete', fullText };
}

/**
 * Return the indices of the next `\n\n` or `\r\n\r\n` separator in
 * `buffer`, or `null` if the buffer doesn't yet contain a full event.
 */
function findEventBoundary(
  buffer: string,
): { index: number; endIndex: number } | null {
  const lf = buffer.indexOf('\n\n');
  const crlf = buffer.indexOf('\r\n\r\n');
  if (lf === -1 && crlf === -1) return null;
  if (lf === -1) return { index: crlf, endIndex: crlf + 4 };
  if (crlf === -1) return { index: lf, endIndex: lf + 2 };
  if (crlf < lf) return { index: crlf, endIndex: crlf + 4 };
  return { index: lf, endIndex: lf + 2 };
}

/**
 * Extract the concatenated `data:` payload from a single SSE event
 * block. Comment lines beginning with `:` and unrelated fields
 * (`event:`, `id:`, `retry:`) are ignored per the SSE spec.
 */
function collectDataLines(rawEvent: string): string[] {
  const out: string[] = [];
  const lines = rawEvent.split(/\r?\n/);
  for (const line of lines) {
    if (line === '' || line.startsWith(':')) continue;
    if (line.startsWith('data:')) {
      // Strip the leading `data:` and a single optional space per spec.
      const value = line.slice(5);
      out.push(value.startsWith(' ') ? value.slice(1) : value);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Internal: response parsing
// ---------------------------------------------------------------------------

/**
 * Pull `candidates[0].content.parts[0].text` out of a parsed Gemini
 * response object. Returns `null` if the path is missing or any node
 * is the wrong shape — `noUncheckedIndexedAccess` forces explicit
 * narrowing at every step.
 */
function extractText(value: unknown): string | null {
  if (!isRecord(value)) return null;
  const candidates = value['candidates'];
  if (!Array.isArray(candidates) || candidates.length === 0) return null;
  const first = candidates[0];
  if (!isRecord(first)) return null;
  const content = first['content'];
  if (!isRecord(content)) return null;
  const parts = content['parts'];
  if (!Array.isArray(parts) || parts.length === 0) return null;
  // Concatenate every textual part; Gemini may split a single response
  // into multiple parts when tool calls or other modalities are mixed
  // in. We ignore non-text parts (e.g. inlineData) safely.
  let text = '';
  for (const part of parts) {
    if (!isRecord(part)) continue;
    const t = part['text'];
    if (typeof t === 'string') text += t;
  }
  return text === '' ? null : text;
}

/**
 * Return the safety / safety-like block reason on a parsed Gemini
 * response, or `null` if the response is not a block. Both top-level
 * `promptFeedback.blockReason` and `candidates[0].finishReason ===
 * 'SAFETY'` count as a block per Gemini's documented response shapes.
 */
function extractBlockReason(value: unknown): string | null {
  if (!isRecord(value)) return null;
  const promptFeedback = value['promptFeedback'];
  if (isRecord(promptFeedback)) {
    const blockReason = promptFeedback['blockReason'];
    if (typeof blockReason === 'string' && blockReason !== '') {
      return blockReason;
    }
  }
  const candidates = value['candidates'];
  if (Array.isArray(candidates) && candidates.length > 0) {
    const first = candidates[0];
    if (isRecord(first)) {
      const finishReason = first['finishReason'];
      if (finishReason === 'SAFETY' || finishReason === 'PROHIBITED_CONTENT') {
        return typeof finishReason === 'string' ? finishReason : 'SAFETY';
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Internal: HTTP error mapping
// ---------------------------------------------------------------------------

/**
 * Convert a non-OK `Response` into a single terminal event.
 *
 * 429 → `rateLimit` with `Retry-After` parsed when present;
 * everything else → `transient` with a redacted reason. The body is
 * read as text so the executor's diagnostic log gets a usable hint
 * (after key redaction); we do NOT surface the body to the UI layer.
 */
async function* mapHttpError(
  response: Response,
  redactKey: (s: string) => string,
): AsyncGenerator<GeminiResponseEvent> {
  if (response.status === 429) {
    const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
    if (retryAfter !== undefined && retryAfter !== null) {
      yield { kind: 'rateLimit', retryAfterSec: retryAfter };
    } else {
      yield { kind: 'rateLimit' };
    }
    return;
  }

  let bodyHint = '';
  try {
    bodyHint = await response.text();
  } catch {
    bodyHint = '';
  }
  const reason = redactKey(`http_${response.status}${bodyHint === '' ? '' : `:${truncate(bodyHint, 200)}`}`);
  yield { kind: 'transient', reason };
}

/**
 * Parse a `Retry-After` header value. Supports both delta-seconds
 * (integer) and HTTP-date forms. Returns `null` if the header is
 * absent or unparseable.
 */
function parseRetryAfter(raw: string | null): number | undefined {
  if (raw === null) return undefined;
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  const asInt = Number.parseInt(trimmed, 10);
  if (Number.isFinite(asInt) && String(asInt) === trimmed && asInt >= 0) {
    return asInt;
  }
  const asDate = Date.parse(trimmed);
  if (Number.isFinite(asDate)) {
    const diffSec = Math.max(0, Math.round((asDate - Date.now()) / 1000));
    return diffSec;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Internal: small utilities
// ---------------------------------------------------------------------------

/**
 * Read a Vite environment variable defensively.
 *
 * `import.meta.env` is replaced at build time but not in some test
 * contexts (e.g., when a unit test imports this module without a Vite
 * transform). The optional-chain plus runtime `unknown` narrowing
 * keeps this importable everywhere; the strict types come from the
 * `vite/client` ambient module declaration.
 */
function readEnv(name: string): string | null {
  const env: unknown =
    typeof import.meta !== 'undefined'
      ? (import.meta as { env?: unknown }).env
      : undefined;
  if (!isRecord(env)) return null;
  const value = env[name];
  if (typeof value === 'string') return value;
  return null;
}

/** `true` iff `value` is a non-null plain object. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Render an unknown thrown value as a short label suitable for the
 * diagnostic log. We avoid surfacing `err.stack` because stack frames
 * can echo the request URL — which contains the key in direct mode —
 * before `redactKey` runs.
 */
function describeError(err: unknown): string {
  if (err instanceof Error) {
    return `${err.name}:${err.message}`;
  }
  if (typeof err === 'string') return err;
  return 'unknown_error';
}

/** Truncate `s` to `max` chars, suffixed with an ellipsis when cut. */
function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max)}…`;
}

/** Strip a single trailing slash so URL composition stays predictable. */
function trimTrailingSlash(s: string): string {
  return s.endsWith('/') ? s.slice(0, -1) : s;
}
